import { FC } from 'react'
import { GeometryObject } from 'geojson'
import { PoiTools } from './PoiTools.tsx'
import { useLayers } from '../../hooks/useLayers.ts'
import { CircleMarker, Polygon } from 'leaflet'
import { useMap } from 'react-leaflet'
import { useAtomValue, useSetAtom } from 'jotai'
import {
    activeGeometryIndexAtom,
    setActiveGeometryAtom,
    setHighlightedGeometryAtom,
} from '@/js/editor/state/editorUiAtoms.ts'
import { activePoiIdAtom } from '@/js/editor/state/poiAtoms.ts'
import { MapPin, VectorPolygon } from 'lucide-react'

type GeometryType = 'Point' | 'Polygon'

type GeometryItemProps = {
    geometry: GeometryObject
    geometryIndex: number
}

const geometryIcons = {
    Point: MapPin,
    Polygon: VectorPolygon,
} satisfies Record<GeometryType, typeof MapPin>

export const GeometryItem: FC<GeometryItemProps> = ({ geometry, geometryIndex: index }) => {
    const activePoiId = useAtomValue(activePoiIdAtom)
    const activeGeometryIndex = useAtomValue(activeGeometryIndexAtom)
    const setActiveGeometry = useSetAtom(setActiveGeometryAtom)
    const setHighlightedGeometry = useSetAtom(setHighlightedGeometryAtom)

    const map = useMap()
    const { findLayerByGeometryIndex } = useLayers()
    const Icon = geometryIcons[geometry.type as GeometryType]

    const selectGeometry = () => {
        const layer = activePoiId === null ? undefined : findLayerByGeometryIndex(activePoiId, index)

        if (activeGeometryIndex === index) {
            setActiveGeometry({
                geometry: null,
                index: null,
            })

            return
        }

        if (!layer) {
            return
        }

        setActiveGeometry({
            geometry,
            index,
        })

        if (layer instanceof CircleMarker) {
            map.panTo(layer.getLatLng())
        } else if (layer instanceof Polygon) {
            const bounds = layer.getBounds()

            if (bounds.isValid()) {
                map.panTo(bounds.getCenter())
            }
        }
    }

    return (
        <div className={'flex flex-col relative'}>
            <div
                className={'border-b'}
                onMouseEnter={() => setHighlightedGeometry(geometry)}
                onMouseLeave={() => setHighlightedGeometry(null)}
                onClick={selectGeometry}
            >
                <div
                    className={`flex flex-row text-sm items-center py-2 my-1 rounded-md hover:bg-mm-bg2 ${activeGeometryIndex === index ? 'bg-mm-bg2' : ''}`}
                >
                    <Icon className={'pl-2'} />
                    <p className={'px-2 flex-1'}>
                        {geometry.type === 'Point' ? 'Punkt' : geometry.type === 'Polygon' ? 'Fläche' : geometry.type}
                    </p>
                </div>
            </div>

            <div
                className={`absolute max-w-fit py-1 my-1.5 pr-2 pl-5 right-0 top-0 transition-transform duration-500 transform ${
                    activeGeometryIndex !== index ? 'translate-x-full' : ''
                } `}
            >
                <PoiTools direction={'horizontal'} />
            </div>
        </div>
    )
}
